import React from 'react'
import { Box } from '@mui/material'
import { DashboardConfig } from '../../../types/gotypes'
import { sidebarTypeForCell } from './WidgetPlaceholder'

/** A stable color per widget category, so a thumbnail reads as a structural map at a glance. */
const COLOR_BY_TYPE: Record<string, string> = {
  title_hero: '#94a3b8',
  text: '#cbd5e1',
  callout: '#fbbf24',
  chart: '#60a5fa',
  table: '#2dd4bf',
  multi_table_analysis: '#a78bfa',
  agent_artifacts: '#818cf8',
  kpi_card: '#4ade80',
  image: '#f9a8d4',
  video: '#f472b6',
  toc: '#e2e8f0',
  spacer: '#f1f5f9',
}

interface TemplateThumbnailProps {
  config: DashboardConfig
  /** Which page's layout to preview — templates with multiple tabs preview the first by default. */
  pageIndex?: number
  height?: number
}

/**
 * A structural preview rendered directly from the template's real layout —
 * one colored block per widget, sized by its row-share, stacked row by row —
 * rather than a generic category icon. This is what the issue calls "actual
 * layout previews so users can see what they're choosing."
 */
const TemplateThumbnail: React.FC<TemplateThumbnailProps> = ({ config, pageIndex = 0, height = 120 }) => {
  const targetLayout = config.pages?.[pageIndex]?.layout || config.layout

  return (
    <Box
      sx={{
        height,
        p: 1,
        bgcolor: 'background.default',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        overflow: 'hidden',
      }}
    >
      {targetLayout.rows.map((r, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 0.5, flex: 1, minHeight: 0 }}>
          {r.cells.map((cell, j) => (
            <Box
              key={j}
              sx={{
                flex: cell.display_props?.width ? `0 0 ${cell.display_props.width}%` : 1,
                minWidth: 0,
                bgcolor: COLOR_BY_TYPE[sidebarTypeForCell(cell.widget_type, cell.table_graph_data?.view_mode)] || '#e2e8f0',
                borderRadius: 0.5,
                opacity: 0.85,
              }}
            />
          ))}
        </Box>
      ))}
    </Box>
  )
}

export default TemplateThumbnail
